import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

export interface AwsRumEnvironmentConfig {
  enabled: boolean;
  appName: string;
  environmentName: string;
  applicationId: string;
  applicationVersion: string;
  applicationRegion: string;
  identityPoolId: string;
  guestRoleArn?: string;
  sessionSampleRate?: number;
  allowCookies?: boolean;
  enableXRay?: boolean;
}

declare global {
  interface Window {
    awsRum?: AwsRum;
  }
}

type RumEventData = Record<string, unknown>;

const MAX_RUM_STRING_LENGTH = 400;
const SENSITIVE_KEY_PATTERN = /(token|authorization|password|secret|cookie)/i;

export function initializeAwsRum(rumConfig: AwsRumEnvironmentConfig): void {
  if (!rumConfig.enabled || !rumConfig.applicationId || !rumConfig.identityPoolId) {
    return;
  }

  try {
    const config: AwsRumConfig = {
      sessionSampleRate: rumConfig.sessionSampleRate ?? 1,
      identityPoolId: rumConfig.identityPoolId,
      endpoint: `https://dataplane.rum.${rumConfig.applicationRegion}.amazonaws.com`,
      telemetries: ['errors', 'http', 'performance'],
      allowCookies: rumConfig.allowCookies ?? true,
      enableXRay: rumConfig.enableXRay ?? false,
      cookieAttributes: {
        unique: true,
      },
      sessionAttributes: {
        app: rumConfig.appName,
        environment: rumConfig.environmentName,
      },
    };

    if (rumConfig.guestRoleArn) {
      config.guestRoleArn = rumConfig.guestRoleArn;
    }

    window.awsRum = new AwsRum(
      rumConfig.applicationId,
      rumConfig.applicationVersion,
      rumConfig.applicationRegion,
      config,
    );
  } catch (error) {
    console.warn('AWS RUM init failed:', error);
  }
}

export function recordRumEvent(type: string, data: RumEventData = {}): void {
  try {
    window.awsRum?.recordEvent(type, sanitizeRumRecord(addDefaultRumData(data)));
  } catch (error) {
    console.warn('AWS RUM event failed:', error);
  }
}

export function recordRumError(reason: string, data: RumEventData = {}): void {
  const payload = sanitizeRumRecord(addDefaultRumData({ reason, ...data }));
  const error = new Error(buildRumErrorMessage(reason, payload));
  error.name = 'AuthSessionRumError';

  try {
    window.awsRum?.recordError(error);
    window.awsRum?.recordEvent('auth.error', payload);
  } catch (rumError) {
    console.warn('AWS RUM error failed:', rumError);
  }
}

function addDefaultRumData(data: RumEventData): RumEventData {
  return {
    ...data,
    route: window.location.pathname,
    hasQueryString: window.location.search.length > 0,
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    timestamp: new Date().toISOString(),
  };
}

function buildRumErrorMessage(reason: string, data: RumEventData): string {
  const route = typeof data['route'] === 'string' ? data['route'] : 'unknown-route';
  const status = data['status'] ? ` status=${data['status']}` : '';

  return `Auth session issue: ${reason} route=${route}${status}`;
}

function sanitizeRumRecord(value: RumEventData): RumEventData {
  return sanitizeRumValue(value) as RumEventData;
}

function sanitizeRumValue(value: unknown, depth = 0): unknown {
  if (depth >= 4) {
    return '[Max depth reached]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > MAX_RUM_STRING_LENGTH ? `${trimmed.slice(0, MAX_RUM_STRING_LENGTH - 1)}…` : trimmed;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeRumValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, 30)
        .map(([key, entryValue]) => [
          key,
          SENSITIVE_KEY_PATTERN.test(key) ? '[Redacted]' : sanitizeRumValue(entryValue, depth + 1),
        ]),
    );
  }

  return String(value);
}
